import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { DateTime } from 'luxon';
import { BehaviorSubject } from 'rxjs';
import { KeyValuePairModel } from '../models/common.models';

export type FilterStateValue =
  | KeyValuePairModel<number>
  | KeyValuePairModel<string>
  | DisplayValuePairModel
  | string
  | DateTime
  | number;
export type FilterStateValues = FilterStateValue[];
export interface FilterState {
  key: string;
  label: string;
  type: string;
  values: FilterStateValues;
  isGlobal: boolean;
  isHidden?: boolean;
}

export interface DisplayValuePairModel {
  value: string;
  display: string;
}

export function isKeyValuePair(
  value: FilterStateValue
): value is KeyValuePairModel<number> | KeyValuePairModel<string> {
  return (
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    'id' in value &&
    'name' in value
  );
}

export function isDisplayValuePair(
  value: FilterStateValue
): value is DisplayValuePairModel {
  return (
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    'value' in value &&
    'display' in value
  );
}

@Injectable({
  providedIn: 'root',
})
export class FilterStateService {
  private storageKey = 'filterState';
  private pageName: string ='';
  private sites : string = 'sites';

  filterState = {};
  resetClicked: BehaviorSubject<boolean> = new BehaviorSubject(false);
  updateOccurred: BehaviorSubject<string> = new BehaviorSubject(null);

  constructor(
    private router: Router
  ) {
    const storedFilters = localStorage.getItem(this.storageKey);
    // storedfilters used to be a flat item of filter objects, but will now be set per pages
    if (storedFilters) {
      let filters;
      try {
        filters = JSON.parse(storedFilters);
      } catch (e) {
        localStorage.removeItem(this.storageKey);
      }

      this.filterState = filters;
      if (!this.filterState) {
        this.filterState = {};
      }

      if (Object.keys(this.filterState).length) {
        Object.keys(this.filterState).forEach((page) => {
          Object.keys(this.filterState[page]).forEach((filter) => {
            if (filter == undefined || filter == 'undefined') {
              return;
            }
            if (!this.filterState[this.pageName] && this.pageName !== '') {
              this.filterState[this.pageName] = {};
            }

            this.updateOccurred.next(filter);
          });
        });
      }
    }
  }

  /**
   * Sets the given value for the given key on the filterState object.
   * @param name Name of the key to set the value of.
   * @param values The values to set.
   * @param type The type of input it is. Options: ```text```, ```simple-select```, ```checkbox```, ```custom-select```, ```date```, ```date-range```, '''number-range'''
   * @param label The label text to describe the filter.
   * @param isGlobal Flag signalling whether this filter is the global site filter
   */
  setValue(
    name: string,
    values: FilterStateValues,
    type: string,
    label: string,
    isGlobal: boolean = false
  ): void {
    if (this.pageName === '') {
      return;
    }

    if (!this.filterState[this.pageName]) {
      this.filterState[this.pageName] = {}; // Could be that its not allowing the text as a key in an array
    }
    this.filterState[this.pageName][name] = {
      key: name,
      label: label,
      type: type,
      values: values,
      isGlobal: isGlobal,
    };
    this.updateOccurred.next(name);
    this.saveFilter();
  }

  /**
   * Get the selected value from the filterState object for a given key.
   * @param name string: Name of the key to return the value of.
   * @returns The values from the filters for the given key.
   */
  getValue(name: string): FilterState {

  

    if (this.filterState[this.pageName] && this.pageName !== '') {
      if (this.filterState[this.pageName][name]) {
        return this.filterState[this.pageName][name];
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return {} as any;
  }

  /**
   * Gets all values in the filterState.
   * @returns FilterState object containing all values selected by name as keys.
   */
  getValues(): FilterState[] {
    if (this.pageName !== '' && this.filterState[this.pageName]) {
      
      return this.filterState[this.pageName];
    }  
    return [];
  }

  /**
   * Resets the filterState object, clearing all the selected filters, then sends the reset message to children components.
   */
  resetValues(): void {
    // Only reset non-global filters
    for (const key in this.filterState[this.pageName]) {
      if (!this.filterState[this.pageName][key].isGlobal) {
        this.resetKey(key);
      }
    }

    this.resetClicked.next(true);
  }

  /**
   * Remove a value from the filterState given the key and values index.
   * @param name String: The key to remove the value from.
   * @param index Int: The index of the array to remove.
   */
  resetValue(name: string, index: number): void {
    this.filterState[this.pageName][name].values.splice(index, 1);
    if(name === this.sites)
    {
         let commonvalue = this.filterState[this.pageName][name].values;
    
        if(commonvalue.length === 0)
        {
           localStorage.removeItem(this.sites);
        }
        else
        {
          let selectedsites = [];
           commonvalue.forEach(s=>{  selectedsites.push(s.id)})
           localStorage.setItem(this.sites, selectedsites.join(','));
        }
    }


    this.updateOccurred.next(name);
    this.saveFilter();
  }

  /**
   * Resets a key to nothing. Used to reset ranges.
   * @param key String: They key to reset to nothing.
   */
  resetKey(key: string): void {
    if (this.filterState[this.pageName] && this.pageName !== '') {
      delete this.filterState[this.pageName][key];
      
      this.updateOccurred.next(key);
      this.saveFilter();
    }
  }

  saveFilter(): void {
    localStorage.setItem(this.storageKey, JSON.stringify(this.filterState));
  }

  setPageName(page: string) {
    this.pageName = page;
  }

  loadPageFilters() {
    const storedFilters = this.getValues();

    Object.keys(storedFilters).forEach((key, index) => {
      // Timeout so that all filters are read.
      // Issue without timeout only the last message is
      // handled and will not populate other fields
      setTimeout(() => {
        this.updateOccurred.next(key);
      }, index * 30); // Adjust the delay (30ms) as needed
    });
  }
}
